import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal } from '@angular/core';
import { Lizenz } from '../../core/enums/lizenz';
import { AdresseService } from '../../core/http/adresse.service';
import { ProjektService } from '../../core/http/projekt.service';
import { UrpKbService } from '../../core/http/urp/urp-kb.service';
import { UrpTagService } from '../../core/http/urp/urp-tag.service';
import { GlobalSettingService } from '../../core/services/global-setting-service';
import { LicenseControllerService } from '../../core/services/license-controller.service';
import { SessionStoreService } from '../../core/services/session-store.service';
import { UserSettingService } from '../../core/services/user-setting-service';
import { VersionCheckerService } from '../../core/services/version-checker.service';
import { MenuComponent } from '../../menu/menu/menu.component';
import { LoaderRefreshComponent } from '../../shared/loader-refresh/loader-refresh.component';
import { LoaderComponent } from '../../shared/loader/loader.component';
import { AdDashboardcardComponent } from '../components/ad-dashboardcard/ad-dashboardcard.component';
import { ArpDashboardcardComponent } from '../components/arp-dashboardcard/arp-dashboardcard.component';
import { BjDashboardcardComponent } from '../components/bj-dashboardcard/bj-dashboardcard.component';
import { PrDashboardcardComponent } from '../components/pr-dashboardcard/pr-dashboardcard.component';
import { QrDashboardcardComponent } from '../components/qr-dashboardcard/qr-dashboardcard.component';
import { UrpDashboardcardComponent } from '../components/urp-dashboardcard/urp-dashboardcard.component';
import { DashboardItem } from './dashboard-item';

@Component({
  selector: 'bpw-b2-dashboardcard',
  templateUrl: './dashboard.component.html',
  standalone: true,
  imports: [
    MenuComponent,
    LoaderComponent,
    LoaderRefreshComponent,
    AdDashboardcardComponent,
    ArpDashboardcardComponent,
    BjDashboardcardComponent,
    PrDashboardcardComponent,
    QrDashboardcardComponent,
    UrpDashboardcardComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DashboardComponent implements OnInit {
  adressDashboardCardData = signal<DashboardItem | null>(null);
  projektDashboardCardData = signal<DashboardItem | null>(null);
  urpDashboardCardData = signal<DashboardItem | null>(null);
  arpDashboardCardData = signal<DashboardItem | null>(null);
  bjDashboardCardData = signal<DashboardItem | null>(null);
  qrDashboardCardData = signal<DashboardItem | null>(null);

  loadComplete = signal(false);
  refreshComplete = signal(false);
  loadSettingsFailed = signal(false);

  private readonly _adresseService = inject(AdresseService);
  private readonly _projektService = inject(ProjektService);
  private readonly _urpTagService = inject(UrpTagService);
  private readonly _kbService = inject(UrpKbService);
  private readonly _licenseController = inject(LicenseControllerService);
  private readonly _sessionStoreService = inject(SessionStoreService);
  private readonly _versionCheckerService = inject(VersionCheckerService);
  private readonly _userSettingService = inject(UserSettingService);
  private readonly _globalSettingService = inject(GlobalSettingService);

  ngOnInit(): void {
    const promises: Promise<void>[] = [];
    const refreshPromises: Promise<unknown>[] = [];
    this.loadComplete.set(false);
    this.refreshComplete.set(false);
    this.loadSettingsFailed.set(false);
    if (!this._userSettingService.settings) {
      this._userSettingService.loadSettings().catch(() => this.loadSettingsFailed.set(true));
    }
    if (!this._sessionStoreService.keyExists('urpDatum')) {
      this._sessionStoreService.setData('urpDatum', new Date().toJSON().substring(0, 10));
    }
    if (!this._sessionStoreService.keyExists('arpDatum')) {
      this._sessionStoreService.setData('arpDatum', new Date().toJSON().substring(0, 10));
    }
    //Daten refreshen
    refreshPromises.push(this._projektService.refreshProjekte());
    refreshPromises.push(this._projektService.refreshProjektRechte());
    refreshPromises.push(this._adresseService.refreshAdressen());
    //Ad-Modul
    promises.push(
      new Promise<void>((resolve) => {
        const dashboardItem = new DashboardItem();
        this.adressDashboardCardData.set(dashboardItem);
        this._licenseController
          .isLicensed(Lizenz.Ad)
          .then((isLicensed) => {
            if (isLicensed) {
              dashboardItem.visible = true;
              this._adresseService
                .getLastUsedAddress()
                .then((adresse) => {
                  dashboardItem.data = adresse;
                })
                .finally(() => {
                  resolve();
                });
            } else {
              resolve();
            }
          })
          .catch(() => {
            resolve();
          });
      }),
    );

    //Pr-Modul
    promises.push(
      new Promise<void>((resolve) => {
        const dashboardItem = new DashboardItem();
        this.projektDashboardCardData.set(dashboardItem);
        this._licenseController
          .isLicensed(Lizenz.Pr)
          .then((isLicensed) => {
            if (isLicensed) {
              dashboardItem.visible = true;
              this._projektService
                .getLastUsedProjekt()
                .then((pr) => {
                  dashboardItem.data = pr;
                })
                .finally(() => {
                  resolve();
                });
              this._projektService.cleanup();
            } else {
              resolve();
            }
          })
          .catch(() => {
            resolve();
          });
      }),
    );

    //Urp-Modul
    promises.push(
      new Promise<void>((resolve) => {
        const dashboardItem = new DashboardItem();
        this.urpDashboardCardData.set(dashboardItem);
        this._licenseController
          .isLicensed(Lizenz.Urp)
          .then((isLicensed) => {
            if (isLicensed) {
              dashboardItem.visible = true;
              refreshPromises.push(this._kbService.refreshKbs());
            }
          })
          .finally(() => {
            resolve();
          });
        this._urpTagService.cleanupBas();
      }),
    );

    //Arp-Modul
    if (this._versionCheckerService.hasRequiredBauplusVersion('10.36.3.0')) {
      promises.push(
        new Promise<void>((resolve) => {
          const dashboardItem = new DashboardItem();
          this.arpDashboardCardData.set(dashboardItem);
          this._licenseController
            .isLicensed(Lizenz.Arp)
            .then((isLicensed) => {
              if (isLicensed) {
                dashboardItem.visible = true;
              }
            })
            .finally(() => {
              resolve();
            });
        }),
      );
    }

    //Bj-Modul
    promises.push(
      new Promise<void>((resolve) => {
        const dashboardItem = new DashboardItem();
        this.bjDashboardCardData.set(dashboardItem);
        this._licenseController
          .isLicensed(Lizenz.Bj)
          .then((isLicensed) => {
            if (isLicensed) {
              dashboardItem.visible = true;
              this._projektService
                .getLastUsedBaujournalProjekt()
                .then((bjPr) => {
                  dashboardItem.data = bjPr;
                })
                .finally(() => {
                  resolve();
                });
            } else {
              resolve();
            }
          })
          .catch(() => {
            resolve();
          });
      }),
    );

    //QR-Scanner
    promises.push(
      new Promise<void>((resolve) => {
        const dashboardItem = new DashboardItem();
        this.qrDashboardCardData.set(dashboardItem);
        const loadGlobalSettings = this._globalSettingService.settings
          ? Promise.resolve()
          : this._globalSettingService.loadSettings();
        // Achtung: Das Promise lädt die globalen Settings (einmalig beim Login an der WebApp), dieses einmalige Laden muss immer gemacht werden, das Promise
        //          muss also unbedingt immer ausgeführt werden, vorher dürfen keine ifs eingefügt werden, welche die Ausführung es Promise verhindern
        loadGlobalSettings
          .then(() => {
            this._licenseController
              .isLicensed(Lizenz.QrScanner)
              .then((isLicensed) => {
                dashboardItem.visible = isLicensed && !!this._globalSettingService.settings?.qrScannerAktiv;
              })
              .finally(() => resolve());
          })
          .catch(() => {
            this.loadSettingsFailed.set(true);
            resolve();
          });
      }),
    );

    Promise.all(promises).then(() => {
      this.loadComplete.set(true);
    });
    Promise.all(refreshPromises).finally(() => {
      this.refreshComplete.set(true);
    });
  }
}
